/**
 * StreamX Landing — Cinematic OTT-grade rebuild (Sprint 2).
 * --------------------------------------------------------------------
 * Replaces the legacy LiveX landing with a Hudl/ESPN/Twitch-grade
 * sports broadcasting experience.  The page sells academy
 * transformation outcomes — NOT features — through:
 *
 *   • Obsidian backgrounds (#0A0A0A) with cinematic gradient orbs
 *   • Glass-morphism cards (white/[0.02] + 1px borders + 24px blur)
 *   • Red/orange brand gradients (#FF3B30 → #990000)
 *   • Neon-green LIVE indicators with glow (#00FF00)
 *   • Poppins-heavy typography, tight tracking, large hero
 *   • Outcomes-first copy: prestige · parent engagement · permanence
 *
 * What is preserved from the previous build:
 *   • Auth context + role-aware CTAs
 *   • `/api/livex/network/pulse` integration (live stats strip)
 *   • <LivexPublicWall /> embed (canonical live-across surface)
 *   • <LiveDot /> primitive (glowing live indicator)
 *
 * What changed:
 *   • Section ordering matches the user's verbatim spec
 *   • Visual layer reframed to OTT-grade
 *   • Copy rewritten to lead with academy outcomes
 */
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import {
  Radio, Play, Trophy, Award, Heart, ShieldCheck, TrendingUp, Crown,
  Film, Eye, Sparkles, Camera, Clock, ChevronRight, Star, Zap, Users,
  Megaphone, GraduationCap, Banknote,
} from 'lucide-react';
import { STREAMX_BRAND, STREAMX_PLAN_PRO, STREAMX_TAGLINE } from '@/lib/brand';
import LivexPublicWall from '@/components/livex/LivexPublicWall';
import LiveDot from '@/components/livex/LiveDot';
import StreamXNavTicker from '@/components/livex/StreamXNavTicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────
// Data hooks
// ─────────────────────────────────────────────────────────────────────

function useLivePulse() {
  const [pulse, setPulse] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchPulse = async () => {
      try {
        const r = await fetch(`${API}/livex/network/pulse`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setPulse(data);
      } catch (_) { /* keep page resilient */ }
    };
    fetchPulse();
    const id = setInterval(fetchPulse, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return pulse;
}

function useCounter(end, duration = 1600) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    if (!end || end <= 0) { setCount(0); return; }
    let s = 0;
    const step = Math.max(1, Math.floor(end / (duration / 16)));
    const t = setInterval(() => {
      s += step;
      if (s >= end) { setCount(end); clearInterval(t); }
      else setCount(s);
    }, 16);
    return () => clearInterval(t);
  }, [inView, end, duration]);
  return { count, ref };
}

function Anim({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 18 }}
      animate={v ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────

export default function StreamX() {
  const { user } = useAuth();
  const pulse = useLivePulse();

  const isStreamer = ['academy_owner', 'super_admin', 'coach', 'employee'].includes(user?.role || '');
  const startHref = user ? '/streamx/studio' : `/login?next=${encodeURIComponent('/streamx/studio')}`;
  const watchHref = '/streamx/live';
  const pricingHref = '/streamx/pricing';

  useEffect(() => {
    document.title = `${STREAMX_BRAND} — Professional Sports Broadcasting for Academies | Out-Play`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
    m.setAttribute('content',
      `Turn your academy into a professional sports broadcast network. Stream matches live, share highlights, engage parents, and grow revenue with ${STREAMX_BRAND} by Out-Play.`);
  }, []);

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden"
      style={{ background: '#0A0A0A' }}
      data-testid="streamx-landing"
    >
      <Nav user={user} isStreamer={isStreamer} startHref={startHref} pulse={pulse} />
      <Hero pulse={pulse} startHref={startHref} watchHref={watchHref} />
      <LiveStatsStrip pulse={pulse} />
      <WhyAcademiesChooseStreamX />
      <LiveAcrossStreamX />
      <ReplayShowcase />
      <StoriesSection />
      <AcademyPrestigeLayer />
      <PricingTeaser pricingHref={pricingHref} />
      <Footer />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Nav — sticky glass top bar with brand lockup + glowing LIVE indicator
// ─────────────────────────────────────────────────────────────────────

function Nav({ user, isStreamer, startHref, pulse }) {
  // StreamX Sprint 3c — LIVE pulse indicator.
  // When `live_count > 0` the "Watch Live" nav link grows a calm,
  // slow heartbeat pulse + brighter hue + soft shadow bloom + tooltip
  // showing exact academy count.  When zero, dim grey, no pulse.
  const liveCount = pulse?.live_count || 0;
  const isLive = liveCount > 0;
  const liveTitle = isLive
    ? `${liveCount} ${liveCount === 1 ? 'academy is' : 'academies are'} live right now`
    : 'No academies streaming right now';
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl border-b"
      style={{
        background: 'rgba(0, 0, 0, 0.6)',
        borderColor: 'rgba(255, 255, 255, 0.08)',
      }}
      data-testid="streamx-nav"
    >
      <div className="h-16 sm:h-20">
      <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 flex items-center justify-between">
        <Link to="/streamx" className="flex items-center gap-2.5 group">
          <div className="relative">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center"
                 style={{ background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)' }}>
              <Radio className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="absolute -inset-1 rounded-lg opacity-50 group-hover:opacity-100 transition-opacity blur-md"
                 style={{ background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)', zIndex: -1 }} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-bold text-lg sm:text-xl tracking-tight text-white">
              {STREAMX_BRAND}
            </span>
            <span className="hidden sm:block text-[9px] uppercase tracking-[0.18em] text-zinc-500 -mt-0.5">
              by Out-Play
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-zinc-400">
          <Link
            to="/streamx/live"
            className="hover:text-white transition-colors flex items-center gap-1.5 relative group"
            title={liveTitle}
            data-testid="streamx-nav-live-link"
          >
            <span className="relative inline-flex h-2.5 w-2.5" aria-hidden>
              {isLive && (
                <>
                  {/* Outer cinematic glow — soft red bloom, slow 2.8s heartbeat */}
                  <motion.span
                    className="absolute inset-[-6px] rounded-full"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.0, 0.55, 0.0] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                      background: 'radial-gradient(circle, rgba(255,59,48,0.65) 0%, rgba(255,59,48,0.0) 70%)',
                      filter: 'blur(4px)',
                    }}
                  />
                  {/* Mid-ring breathing scale — 1 → 1.06 (broadcast-subtle) */}
                  <motion.span
                    className="absolute inline-flex h-full w-full rounded-full"
                    initial={{ opacity: 0.75, scale: 1 }}
                    animate={{ opacity: [0.75, 1, 0.75], scale: [1, 1.06, 1] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ background: '#FF3B30' }}
                  />
                </>
              )}
              <span
                className="relative inline-flex h-2.5 w-2.5 rounded-full transition-colors"
                style={{
                  background: isLive ? '#FF3B30' : 'rgba(148, 163, 184, 0.45)',
                  boxShadow: isLive ? '0 0 10px rgba(255, 59, 48, 0.85)' : 'none',
                }}
                data-testid={isLive ? 'streamx-nav-live-dot-active' : 'streamx-nav-live-dot-dim'}
              />
            </span>
            <span className={isLive ? 'text-white font-semibold' : ''}>Watch Live</span>
            {isLive && (
              <span
                className="text-[9px] uppercase tracking-[0.18em] font-bold ml-1 px-1.5 py-0.5 rounded-md"
                style={{
                  background: 'rgba(255, 59, 48, 0.12)',
                  color: '#FF6B5C',
                  border: '1px solid rgba(255, 59, 48, 0.3)',
                }}
                data-testid="streamx-nav-live-count-badge"
              >
                {liveCount}
              </span>
            )}
          </Link>
          <Link to="/streamx/stories" className="hover:text-white transition-colors">Stories</Link>
          <Link to="/streamx/pricing" className="hover:text-white transition-colors">Pricing</Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <Link
              to={isStreamer ? '/streamx/studio' : '/streamx/dashboard'}
              className="text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-white transition-transform hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)',
                boxShadow: '0 0 20px rgba(255, 59, 48, 0.3)',
              }}
              data-testid="streamx-nav-dashboard-cta"
            >
              {isStreamer ? 'Open Studio' : 'Dashboard'}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-xs sm:text-sm text-zinc-400 hover:text-white transition-colors px-3 py-2"
                data-testid="streamx-nav-login"
              >
                Sign in
              </Link>
              <Link
                to={startHref}
                className="text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-white transition-transform hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)',
                  boxShadow: '0 0 20px rgba(255, 59, 48, 0.3)',
                }}
                data-testid="streamx-nav-start-cta"
              >
                Start Streaming
              </Link>
            </>
          )}
        </div>
      </div>
      </div>
      {/* StreamX Sprint 3d — Ambient "Trending Right Now" ticker.  Auto-hides
          when no academies are live.  Reads the same `pulse` payload — zero
          extra network footprint. */}
      <StreamXNavTicker pulse={pulse} />
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Hero — centered, cinematic, with animated gradient orbs
// ─────────────────────────────────────────────────────────────────────

function Hero({ pulse, startHref, watchHref }) {
  // Phase StreamX Sprint 3b — desktop-only cinematic video loop.
  // The hero ships with a free Pexels cinematic football clip layered
  // BEHIND the gradient orbs at 18% opacity.  The env var
  // `REACT_APP_STREAMX_HERO_VIDEO` lets ops swap the asset without a
  // code change (e.g. once proprietary StreamX footage is ready).
  //
  // Doctrine:
  //   • muted + playsinline + autoplay + loop + preload="metadata"
  //   • desktop-only via CSS (hidden < 768px) — preserves mobile data
  //   • poster fallback covers the gap until the first frame paints
  //   • the `<video>` failure silently no-ops; gradient orbs alone
  //     still feel cinematic.
  const heroVideoSrc = process.env.REACT_APP_STREAMX_HERO_VIDEO
    || 'https://videos.pexels.com/video-files/3327959/3327959-hd_1920_1080_25fps.mp4';
  return (
    <section
      className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 px-4 overflow-hidden"
      data-testid="streamx-hero"
    >
      {/* Cinematic video — desktop only, 18% opacity, blurred for depth */}
      <div
        className="absolute inset-0 -z-20 overflow-hidden hidden md:block"
        aria-hidden
      >
        <video
          src={heroVideoSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: 0.18,
            filter: 'blur(2px) saturate(1.15)',
            transform: 'scale(1.05)',  // hide blur-edge fringing
          }}
          onError={(e) => { try { e.currentTarget.style.display = 'none'; } catch (_) {} }}
          data-testid="streamx-hero-video"
        />
        {/* Cinematic dark vignette over the video so the headline stays legible */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.85) 60%, rgba(10,10,10,0.98) 100%)',
          }}
        />
      </div>

      {/* Animated gradient orbs (mobile shows these without video) */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <motion.div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px]"
          style={{ background: 'radial-gradient(circle, #FF3B30 0%, transparent 70%)' }}
          animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 w-[500px] h-[500px] rounded-full opacity-15 blur-[120px]"
          style={{ background: 'radial-gradient(circle, #FF4500 0%, transparent 70%)' }}
          animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
        {/* Subtle grid texture */}
        <div className="absolute inset-0 opacity-[0.025]"
             style={{
               backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
               backgroundSize: '64px 64px',
             }} />
      </div>

      <div className="max-w-5xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 sm:mb-8"
          style={{
            background: 'rgba(255, 59, 48, 0.08)',
            border: '1px solid rgba(255, 59, 48, 0.25)',
          }}
        >
          <Sparkles className="h-3 w-3" style={{ color: '#FF3B30' }} />
          <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em]"
                style={{ color: '#FF6B5C' }}>
            The New Standard for Academies
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1, ease: 'easeOut' }}
          className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05]"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Turn your academy into a{' '}
          <span
            className="inline-block bg-clip-text text-transparent"
            style={{
              backgroundImage: 'linear-gradient(135deg, #FF3B30 0%, #FF4500 50%, #990000 100%)',
            }}
          >
            professional sports
          </span>
          {' '}broadcast network.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-6 sm:mt-8 text-base sm:text-lg lg:text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed"
        >
          Stream matches live. Share replays. Build academy reputation. Engage
          parents. Grow visibility with <span className="text-white font-semibold">{STREAMX_BRAND}</span>.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <Link
            to={startHref}
            className="group inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 rounded-xl font-bold text-sm sm:text-base text-white w-full sm:w-auto transition-transform hover:scale-[1.03]"
            style={{
              background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)',
              boxShadow: '0 0 30px rgba(255, 59, 48, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
            }}
            data-testid="streamx-hero-cta-start"
          >
            <Camera className="h-4 w-4" />
            Start Streaming
            <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to={watchHref}
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 rounded-xl font-bold text-sm sm:text-base w-full sm:w-auto transition-colors"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              color: '#FFFFFF',
            }}
            data-testid="streamx-hero-cta-watch"
          >
            <LiveDot />
            Watch Live Matches
          </Link>
          <a
            href="#sixty-second-tour"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-zinc-400 hover:text-white transition-colors"
            data-testid="streamx-hero-cta-demo"
          >
            <Play className="h-3.5 w-3.5" />
            Watch demo
          </a>
        </motion.div>

        {pulse?.live_count > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-xs sm:text-sm backdrop-blur-md"
            style={{
              background: 'rgba(0, 255, 0, 0.06)',
              border: '1px solid rgba(0, 255, 0, 0.18)',
            }}
            data-testid="streamx-hero-live-now"
          >
            <span
              className="h-2 w-2 rounded-full animate-pulse"
              style={{ background: '#00FF00', boxShadow: '0 0 12px rgba(0, 255, 0, 0.7)' }}
            />
            <span className="font-semibold text-white">
              {pulse.live_count} {pulse.live_count === 1 ? 'academy is' : 'academies are'} streaming right now
            </span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-400">{pulse.watching || 0} watching</span>
          </motion.div>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Live stats strip — animated counters
// ─────────────────────────────────────────────────────────────────────

function LiveStatsStrip({ pulse }) {
  const liveCount = pulse?.live_count || 0;
  const watching = pulse?.watching || 0;
  const matchesThisWeek = pulse?.matches_this_week || 0;
  const highlightsThisWeek = pulse?.highlights_this_week || 0;

  const stats = [
    { label: 'Academies streaming', value: liveCount, icon: Radio, accent: '#00FF00' },
    { label: 'Currently watching', value: watching, icon: Eye, accent: '#FF6B5C' },
    { label: 'Matches this week', value: matchesThisWeek, icon: Trophy, accent: '#FFA500' },
    { label: 'Highlights this week', value: highlightsThisWeek, icon: Sparkles, accent: '#FF4500' },
  ];

  return (
    <section
      className="border-y"
      style={{
        background: 'rgba(255, 255, 255, 0.015)',
        borderColor: 'rgba(255, 255, 255, 0.06)',
      }}
      data-testid="streamx-live-stats"
    >
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {stats.map((s, i) => (
          <CounterCard key={i} {...s} />
        ))}
      </div>
    </section>
  );
}

function CounterCard({ label, value, icon: Icon, accent }) {
  const { count, ref } = useCounter(value);
  return (
    <div ref={ref} className="flex items-center gap-3 sm:gap-4">
      <div
        className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center shrink-0"
        style={{
          background: `${accent}15`,
          border: `1px solid ${accent}30`,
        }}
      >
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl sm:text-3xl font-black tabular-nums" style={{ fontFamily: 'Poppins, sans-serif' }}>
          {count}
        </div>
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.16em] text-zinc-500 truncate">
          {label}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Why Academies Choose StreamX — bento grid (outcomes-first)
// ─────────────────────────────────────────────────────────────────────

const WHY_CARDS = [
  {
    icon: Crown,
    title: 'Build academy prestige',
    desc: 'Give your athletes a platform that looks like ESPN. Premium branding attracts top talent and keeps your best players loyal.',
    accent: '#FF3B30',
    span: 'lg:col-span-2',
  },
  {
    icon: Heart,
    title: 'Engage parents',
    desc: 'Never let a parent miss a match. High-quality streams delivered to their phones the moment kick-off happens.',
    accent: '#FF4500',
  },
  {
    icon: Film,
    title: 'Permanent replay archives',
    desc: 'Every match indexed, chaptered, and securely stored. Players review footage to build recruitment portfolios that win college scholarships.',
    accent: '#FFA500',
  },
  {
    icon: Megaphone,
    title: 'Sponsor visibility',
    desc: 'Monetize streams with professional lower-thirds, sponsor placements, and revenue share — every match becomes a media asset.',
    accent: '#FF6B5C',
  },
  {
    icon: TrendingUp,
    title: 'Player exposure',
    desc: 'Talent scouts watch StreamX. Highlight clips travel. The next college offer might come from a match you streamed this Saturday.',
    accent: '#FF3B30',
  },
  {
    icon: Banknote,
    title: 'Tournament monetization',
    desc: 'Gate access to high-stakes tournaments with integrated ticketing, pay-per-view, and direct payouts to your academy account.',
    accent: '#990000',
    span: 'lg:col-span-2',
  },
];

function WhyAcademiesChooseStreamX() {
  return (
    <section className="py-24 sm:py-32 px-4 relative" data-testid="streamx-why">
      <div className="max-w-7xl mx-auto">
        <Anim className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-zinc-500 mb-3">
            Why academies choose us
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}>
            We don't sell streaming software.<br />
            <span className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #FF3B30, #FF4500)' }}>
              We sell academy transformation.
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-zinc-400 leading-relaxed">
            Every {STREAMX_BRAND} feature is engineered to upgrade the reputation,
            visibility, and economics of your academy.
          </p>
        </Anim>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {WHY_CARDS.map((card, i) => (
            <Anim key={i} delay={i * 0.04} className={card.span || ''}>
              <WhyCard {...card} />
            </Anim>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyCard({ icon: Icon, title, desc, accent }) {
  return (
    <div
      className="group relative rounded-2xl p-6 sm:p-8 h-full transition-all duration-300 hover:-translate-y-1"
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        backdropFilter: 'blur(12px)',
      }}
      data-testid="streamx-why-card"
    >
      {/* Hover glow */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{
          background: `radial-gradient(circle at top right, ${accent}10, transparent 70%)`,
        }}
      />
      <div
        className="relative h-12 w-12 rounded-xl flex items-center justify-center mb-5"
        style={{
          background: `${accent}12`,
          border: `1px solid ${accent}30`,
        }}
      >
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <h3 className="relative text-xl sm:text-2xl font-bold tracking-tight text-white mb-2"
          style={{ fontFamily: 'Poppins, sans-serif' }}>
        {title}
      </h3>
      <p className="relative text-sm sm:text-base text-zinc-400 leading-relaxed">{desc}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Live Across StreamX — wraps the canonical public wall
// ─────────────────────────────────────────────────────────────────────

function LiveAcrossStreamX() {
  return (
    <section
      className="py-20 sm:py-28 border-y relative"
      style={{
        background: 'rgba(255, 255, 255, 0.015)',
        borderColor: 'rgba(255, 255, 255, 0.06)',
      }}
      data-testid="streamx-live-across"
    >
      <div className="max-w-7xl mx-auto px-4">
        <Anim className="flex flex-wrap items-center justify-between gap-4 mb-8 sm:mb-12">
          <div>
            <div className="inline-flex items-center gap-2 mb-3">
              <span
                className="h-2.5 w-2.5 rounded-full animate-pulse"
                style={{ background: '#00FF00', boxShadow: '0 0 14px rgba(0, 255, 0, 0.8)' }}
              />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em]"
                    style={{ color: '#00FF00' }}>
                LIVE NOW
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight"
                style={{ fontFamily: 'Poppins, sans-serif' }}>
              Live across {STREAMX_BRAND}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-zinc-400">
              Real academies. Real matches. Streaming this second.
            </p>
          </div>
          <Link
            to="/streamx/live"
            className="group inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-orange-400 transition-colors"
            data-testid="streamx-live-across-cta"
          >
            See all live streams
            <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </Anim>

        <Anim delay={0.1}>
          <LivexPublicWall />
        </Anim>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Replay Showcase — Twitch-style cards with chapter markers
// ─────────────────────────────────────────────────────────────────────

const REPLAY_SAMPLES = [
  { title: 'U-15 Football Final', academy: 'VBFC Academy', dur: '92:14', views: 1240, chapters: 7, gradient: 'linear-gradient(135deg, #134e4a, #134e4a 40%, #0a0a0a)' },
  { title: 'Inter-school Cricket Showcase', academy: 'Bengaluru Sports School', dur: '78:30', views: 892, chapters: 5, gradient: 'linear-gradient(135deg, #7c2d12, #7c2d12 40%, #0a0a0a)' },
  { title: 'Basketball Quarter-final', academy: 'Hyderabad Hoops', dur: '54:22', views: 654, chapters: 4, gradient: 'linear-gradient(135deg, #1e3a8a, #1e3a8a 40%, #0a0a0a)' },
];

function ReplayShowcase() {
  return (
    <section id="sixty-second-tour" className="py-24 sm:py-32 px-4" data-testid="streamx-replays">
      <div className="max-w-7xl mx-auto">
        <Anim className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-zinc-500 mb-3">
            Replay Showcase
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}>
            Every match,{' '}
            <span className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #FF3B30, #FF4500)' }}>
              indexed and chaptered.
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-zinc-400 leading-relaxed">
            Auto-generated chapter markers. Top-moments. Recruitment-ready clips.
            Your replay library becomes your academy's prestige asset.
          </p>
        </Anim>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {REPLAY_SAMPLES.map((r, i) => (
            <Anim key={i} delay={i * 0.08}>
              <ReplayCard {...r} />
            </Anim>
          ))}
        </div>

        <Anim delay={0.4} className="mt-10 sm:mt-12 text-center">
          <Link
            to="/streamx/replays"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-transform hover:scale-105"
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
            }}
            data-testid="streamx-replays-cta"
          >
            <Film className="h-4 w-4" />
            Watch Top Moments
            <ChevronRight className="h-4 w-4" />
          </Link>
        </Anim>
      </div>
    </section>
  );
}

function ReplayCard({ title, academy, dur, views, chapters, gradient }) {
  return (
    <div
      className="group rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
      }}
      data-testid="streamx-replay-card"
    >
      {/* Thumbnail */}
      <div
        className="aspect-video relative overflow-hidden"
        style={{ background: gradient }}
      >
        {/* Play glyph */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="h-14 w-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
            style={{
              background: 'rgba(255, 59, 48, 0.92)',
              boxShadow: '0 0 24px rgba(255, 59, 48, 0.5)',
            }}
          >
            <Play className="h-5 w-5 text-white ml-0.5" fill="white" />
          </div>
        </div>
        {/* Duration */}
        <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md text-[11px] font-mono font-bold"
             style={{ background: 'rgba(0, 0, 0, 0.7)' }}>
          {dur}
        </div>
        {/* Chapter dots — Twitch-style timeline scrubber */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5"
             style={{ background: 'rgba(255, 255, 255, 0.15)' }}>
          {Array.from({ length: chapters }).map((_, i) => (
            <span
              key={i}
              className="absolute h-2 w-2 rounded-full -top-0.5"
              style={{
                left: `${((i + 1) / (chapters + 1)) * 100}%`,
                background: '#FF3B30',
                boxShadow: '0 0 6px rgba(255, 59, 48, 0.7)',
              }}
            />
          ))}
        </div>
      </div>
      <div className="p-4 sm:p-5">
        <div className="text-sm sm:text-base font-bold text-white mb-1 line-clamp-1">{title}</div>
        <div className="text-xs text-zinc-400">{academy}</div>
        <div className="mt-3 flex items-center gap-3 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1">
            <Eye className="h-3 w-3" /> {views.toLocaleString()}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {chapters} chapters
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stories — cinematic editorial cards
// ─────────────────────────────────────────────────────────────────────

const STORY_SAMPLES = [
  {
    eyebrow: 'This Week',
    title: 'The U-15 keeper who saved seven shots in eight minutes.',
    academy: 'Vijayawada · Football',
    gradient: 'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.75) 65%, rgba(0,0,0,0.95) 100%), linear-gradient(135deg, #7c2d12 0%, #134e4a 100%)',
  },
  {
    eyebrow: 'Rising Star',
    title: 'How a school cricket academy built a 12,000-strong replay audience.',
    academy: 'Bengaluru · Cricket',
    gradient: 'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.75) 65%, rgba(0,0,0,0.95) 100%), linear-gradient(135deg, #1e3a8a 0%, #5b21b6 100%)',
  },
  {
    eyebrow: 'Feature',
    title: 'Why every match should end with a 60-second highlight reel.',
    academy: 'StreamX Editorial',
    gradient: 'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.75) 65%, rgba(0,0,0,0.95) 100%), linear-gradient(135deg, #991b1b 0%, #b45309 100%)',
  },
];

function StoriesSection() {
  return (
    <section
      className="py-24 sm:py-32 px-4 relative"
      style={{ background: 'rgba(255, 255, 255, 0.015)' }}
      data-testid="streamx-stories"
    >
      <div className="max-w-7xl mx-auto">
        <Anim className="flex flex-wrap items-end justify-between gap-4 mb-12 sm:mb-16">
          <div className="max-w-2xl">
            <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-zinc-500 mb-3">
              Weekly editorial
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight"
                style={{ fontFamily: 'Poppins, sans-serif' }}>
              Sports stories worth watching.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-zinc-400 leading-relaxed">
              Curated weekly. Athlete journeys, academy comebacks, the moments
              that made the highlight reel.
            </p>
          </div>
          <Link
            to="/streamx/stories"
            className="group inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-orange-400 transition-colors"
            data-testid="streamx-stories-cta"
          >
            Browse all stories
            <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </Anim>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {STORY_SAMPLES.map((s, i) => (
            <Anim key={i} delay={i * 0.08}>
              <StoryCard {...s} />
            </Anim>
          ))}
        </div>
      </div>
    </section>
  );
}

function StoryCard({ eyebrow, title, academy, gradient }) {
  return (
    <Link
      to="/streamx/stories"
      className="group block rounded-2xl overflow-hidden relative aspect-[3/4] transition-transform hover:-translate-y-1"
      style={{ background: gradient }}
      data-testid="streamx-story-card"
    >
      <div className="absolute inset-0 p-5 sm:p-6 flex flex-col justify-end">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-orange-400 mb-2">
          {eyebrow}
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight mb-3"
            style={{ fontFamily: 'Poppins, sans-serif' }}>
          {title}
        </h3>
        <div className="text-xs text-zinc-300 inline-flex items-center gap-1.5">
          {academy}
          <ChevronRight className="h-3 w-3 opacity-60 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Academy Prestige Layer — Featured / Rising / Most Watched
// ─────────────────────────────────────────────────────────────────────

const PRESTIGE_CARDS = [
  {
    badge: 'Featured This Week',
    icon: Star,
    accent: '#FFD700',
    title: 'VBFC Football Academy',
    sub: 'Top 1% of streaming uptime · 18 matches broadcast this month',
    cta: 'See top moments',
  },
  {
    badge: 'Rising Academy',
    icon: TrendingUp,
    accent: '#00FF00',
    title: 'Hyderabad Hoops',
    sub: 'Replay views up 312% month-over-month · 4 new sponsorships',
    cta: 'Watch their journey',
  },
  {
    badge: 'Most Watched Match',
    icon: Trophy,
    accent: '#FF3B30',
    title: 'U-15 District Cup Final',
    sub: '14,820 watching live · 47,000 replay views in 72 hours',
    cta: 'Re-watch the match',
  },
];

function AcademyPrestigeLayer() {
  return (
    <section className="py-24 sm:py-32 px-4 relative" data-testid="streamx-prestige">
      <div className="max-w-7xl mx-auto">
        <Anim className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-zinc-500 mb-3">
            Academy prestige layer
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}>
            Where the best academies{' '}
            <span className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #FFD700, #FF4500)' }}>
              get spotlighted.
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-zinc-400 leading-relaxed">
            Streamed consistently. Watched widely. Rewarded with badges that
            convert parent trust and sponsor interest into long-term growth.
          </p>
        </Anim>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {PRESTIGE_CARDS.map((p, i) => (
            <Anim key={i} delay={i * 0.06}>
              <PrestigeCard {...p} />
            </Anim>
          ))}
        </div>
      </div>
    </section>
  );
}

function PrestigeCard({ badge, icon: Icon, accent, title, sub, cta }) {
  return (
    <div
      className="group relative rounded-2xl p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.07)',
      }}
      data-testid="streamx-prestige-card"
    >
      {/* Ribbon glow */}
      <div
        className="absolute -top-20 -right-20 h-40 w-40 rounded-full opacity-25 blur-2xl pointer-events-none"
        style={{ background: accent }}
      />

      <div className="relative">
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] mb-5"
          style={{
            background: `${accent}14`,
            border: `1px solid ${accent}45`,
            color: accent,
          }}
        >
          <Icon className="h-3 w-3" />
          {badge}
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-2"
            style={{ fontFamily: 'Poppins, sans-serif' }}>
          {title}
        </h3>
        <p className="text-sm text-zinc-400 leading-relaxed mb-5">{sub}</p>
        <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-white group-hover:text-orange-400 transition-colors">
          {cta}
          <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Pricing teaser — shimmer-edge CTA
// ─────────────────────────────────────────────────────────────────────

function PricingTeaser({ pricingHref }) {
  return (
    <section className="py-24 sm:py-32 px-4 relative overflow-hidden" data-testid="streamx-pricing-teaser">
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[300px] rounded-full opacity-15 blur-[100px]"
          style={{ background: 'radial-gradient(circle, #FF3B30 0%, transparent 70%)' }}
        />
      </div>

      <Anim className="max-w-3xl mx-auto text-center">
        <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-zinc-500 mb-3">
          Professional streaming, simplified
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-5"
            style={{ fontFamily: 'Poppins, sans-serif' }}>
          One subscription.{' '}
          <span className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #FF3B30, #FF4500)' }}>
            Unlimited broadcasts.
          </span>
        </h2>
        <p className="text-base sm:text-lg text-zinc-400 leading-relaxed mb-8 max-w-2xl mx-auto">
          Live streaming, replay archives, highlight clips, chapter markers,
          sponsor branding, and prestige badges. Everything your academy needs
          to broadcast professionally.
        </p>

        <div
          className="inline-flex items-baseline gap-2 mb-6 px-4 py-2 rounded-xl"
          style={{
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <span className="text-sm uppercase tracking-[0.2em] text-zinc-400">{STREAMX_PLAN_PRO}</span>
          <span className="text-zinc-600">·</span>
          <span className="text-2xl sm:text-3xl font-black text-white"
                style={{ fontFamily: 'Poppins, sans-serif' }}>
            ₹299
          </span>
          <span className="text-sm text-zinc-400">/ month</span>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={pricingHref}
            className="group relative inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-bold text-white text-sm sm:text-base transition-transform hover:scale-[1.03] overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)',
              boxShadow: '0 0 30px rgba(255, 59, 48, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
            }}
            data-testid="streamx-pricing-cta"
          >
            {/* Shimmer */}
            <span
              className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.18), transparent)',
              }}
            />
            <Crown className="h-4 w-4 relative" />
            <span className="relative">Upgrade to {STREAMX_PLAN_PRO}</span>
            <ChevronRight className="h-4 w-4 relative group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to={pricingHref}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-zinc-300 hover:text-white transition-colors"
            data-testid="streamx-pricing-compare"
          >
            See full plan comparison
          </Link>
        </div>
      </Anim>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer
      className="border-t py-12 sm:py-16 px-4"
      style={{
        borderColor: 'rgba(255, 255, 255, 0.06)',
        background: '#050505',
      }}
      data-testid="streamx-footer"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2 md:col-span-2">
            <Link to="/streamx" className="inline-flex items-center gap-2.5 mb-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center"
                   style={{ background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)' }}>
                <Radio className="h-4 w-4 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-bold text-xl tracking-tight text-white">
                  {STREAMX_BRAND}
                </span>
                <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 -mt-0.5">
                  by Out-Play
                </span>
              </div>
            </Link>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-md">
              {STREAMX_TAGLINE}. Built specifically for sports academies — turn
              every match into a media asset that grows your academy's brand,
              audience, and revenue.
            </p>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-4">
              Platform
            </div>
            <ul className="space-y-2.5 text-sm text-zinc-400">
              <li><Link to="/streamx/live" className="hover:text-white transition-colors">Watch live</Link></li>
              <li><Link to="/streamx/replays" className="hover:text-white transition-colors">Replays</Link></li>
              <li><Link to="/streamx/stories" className="hover:text-white transition-colors">Stories</Link></li>
              <li><Link to="/streamx/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-4">
              Out-Play
            </div>
            <ul className="space-y-2.5 text-sm text-zinc-400">
              <li><Link to="/" className="hover:text-white transition-colors">Academy OS</Link></li>
              <li><Link to="/arenax" className="hover:text-white transition-colors">ArenaX</Link></li>
              <li><Link to="/login" className="hover:text-white transition-colors">Sign in</Link></li>
            </ul>
          </div>
        </div>

        <div className="pt-6 border-t flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-600"
             style={{ borderColor: 'rgba(255, 255, 255, 0.04)' }}>
          <div>© {new Date().getFullYear()} {STREAMX_BRAND} by Out-Play. All rights reserved.</div>
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" />
            <span>WCAG AA · GDPR-ready · Made in India</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
